import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import Cookies from 'js-cookie'
import { useModalA11y } from '@/components/accessibility'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  functional: boolean
}

export function CookieNotification() {
  const { t } = useTranslation('common')
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false,
  })

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = Cookies.get('cookie-consent')
    if (!cookieConsent) {
      setShowBanner(true)
    } else {
      // Load saved preferences
      const savedPreferences = Cookies.get('cookie-preferences')
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences))
      }
    }
  }, [])

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    }
    setPreferences(allAccepted)
    savePreferences(allAccepted)
    setShowBanner(false)
  }

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    }
    setPreferences(onlyNecessary)
    savePreferences(onlyNecessary)
    setShowBanner(false)
  }

  const handleSavePreferences = () => {
    savePreferences(preferences)
    setShowBanner(false)
    setShowPreferences(false)
  }

  const savePreferences = (prefs: CookiePreferences) => {
    const cookieOpts = {
      expires: 365,
      sameSite: 'lax' as const,
      secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
    }
    Cookies.set('cookie-consent', 'true', cookieOpts)
    Cookies.set('cookie-preferences', JSON.stringify(prefs), cookieOpts)

    // Apply preferences to actual cookies
    if (!prefs.analytics) {
      // Disable analytics cookies and remove any previously-stored analytics session id
      Cookies.remove('_ga')
      Cookies.remove('_gid')
      Cookies.remove('_gat')
      try { sessionStorage.removeItem('analytics_session_id') } catch { /* ignore */ }
    }

    if (!prefs.marketing) {
      // Disable marketing cookies
      Cookies.remove('_fbp')
      Cookies.remove('_fbc')
    }

    // Notify consent-aware components so they can re-evaluate
    // without requiring a full page reload. ePrivacy Art. 5(3) compliance.
    try {
      window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: prefs }))
    } catch { /* ignore */ }
  }

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return // Can't disable necessary cookies

    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // Accessibility: trap focus and handle Escape inside the preferences dialog.
  const prefsDialogRef = useModalA11y(showPreferences, () => setShowPreferences(false))

  if (!showBanner) return null

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 start-0 end-0 bg-white border-t border-neutral-200 shadow-lg z-[60]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {t('cookieTitle')}
              </h3>
              <p className="text-sm text-neutral-600">
                {t('cookieDescription')}{' '}
                <a href="/privacy-policy" className="text-primary-600 hover:text-primary-500 underline">
                  {t('privacyPolicy')}
                </a>.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {t('managePreferences')}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {t('rejectAll')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {t('acceptAll')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div
            ref={prefsDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-prefs-title"
            aria-describedby="cookie-prefs-description"
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 id="cookie-prefs-title" className="text-2xl font-bold text-neutral-900">{t('cookiePreferences')}</h2>
                <button
                  type="button"
                  onClick={() => setShowPreferences(false)}
                  aria-label="Close cookie preferences"
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <p id="cookie-prefs-description" className="text-neutral-600">
                  {t('cookiePreferencesDescription')}
                </p>

                {/* Necessary Cookies */}
                <div className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-neutral-900">{t('necessaryCookies')}</h3>
                    <div className="flex items-center">
                      <span className="text-sm text-neutral-500 ltr:me-2 rtl:ms-2">{t('alwaysActive')}</span>
                      <div className="w-12 h-6 bg-primary-600 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600">
                    {t('necessaryCookiesDescription')}
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-neutral-900">{t('analyticsCookies')}</h3>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.analytics ? 'bg-primary-600' : 'bg-neutral-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-sm text-neutral-600">
                    {t('analyticsCookiesDescription')}
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-neutral-900">{t('marketingCookies')}</h3>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.marketing ? 'bg-primary-600' : 'bg-neutral-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.marketing ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-sm text-neutral-600">
                    {t('marketingCookiesDescription')}
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-neutral-900">{t('functionalCookies')}</h3>
                    <button
                      onClick={() => togglePreference('functional')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.functional ? 'bg-primary-600' : 'bg-neutral-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.functional ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-sm text-neutral-600">
                    {t('functionalCookiesDescription')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {t('rejectAll')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {t('acceptAll')}
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-secondary-600 border border-transparent rounded-md hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  {t('savePreferences')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
